import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useGetRepInfoQuery, useSetRepInfoMutation } from '../../store/api/saleContractApi'
import { usePagePermission } from '../../hooks/usePagePermission'

interface ChooseRepInfoDialogProps {
  open: boolean
  onClose: () => void
}

export default function ChooseRepInfoDialog({ open, onClose }: ChooseRepInfoDialogProps) {
  const { canEdit } = usePagePermission()
  const { data, isLoading } = useGetRepInfoQuery(undefined, { skip: !open })
  const [setRepInfo, { isLoading: isSaving }] = useSetRepInfoMutation()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedId(null)
      setSaveError(null)
    }
  }, [open])

  const currentId = selectedId ?? data?.currentSignId ?? null
  const rows = data?.items ?? []

  const handleSave = async () => {
    if (currentId == null) return
    setSaveError(null)
    try {
      await setRepInfo(currentId).unwrap()
      onClose()
    } catch {
      setSaveError('Failed to save representative info.')
    }
  }

  const handleCancel = () => {
    setSelectedId(null)
    setSaveError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Choose Representative Info</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {saveError ? <Alert severity="error" sx={{ m: 1.5 }}>{saveError}</Alert> : null}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No representative records found in <code>public.sign_info</code> (Access export name{' '}
            <code>public_sign_info</code> maps to schema <code>public</code>, table <code>sign_info</code>).
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={currentId === row.id}
                  onClick={() => setSelectedId(row.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      size="small"
                      checked={currentId === row.id}
                      onChange={() => setSelectedId(row.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>{row.name ?? '—'}</TableCell>
                  <TableCell>{row.position ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} size="small">Cancel</Button>
        <Box sx={{ position: 'relative' }}>
          <Button
            variant="contained"
            size="small"
            disabled={currentId == null || isSaving || !canEdit}
            onClick={handleSave}
          >
            OK
          </Button>
          {isSaving ? (
            <CircularProgress
              size={18}
              sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-9px', ml: '-9px' }}
            />
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  )
}
